import { useEffect } from 'react';
import { LogManager, LogLevel, TimestampFormat } from '@src/utility/Logger';

/**
 * Configuration options for application logging
 */
export interface LoggingOptions {
  /** Enable global logging (defaults to true in development) */
  enabled?: boolean;
  /** Console logging level */
  logLevel?: LogLevel;
  /** Enable logging in browser console */
  browserConsoleCommands?: boolean;
  /** Timestamp format for log messages */
  timestampFormat?: TimestampFormat;
  /** List of channels to enable by default */
  enabledChannels?: string[];
  /** List of channels to disable by default */
  disabledChannels?: string[];
}

/**
 * Hook that initializes and configures the logging system for the application
 * 
 * This hook should be called once at the application root level to set up
 * application-wide logging preferences. Individual components or features
 * can then configure their own specific logging channels.
 * 
 * @param options Logging configuration options
 */
export function useLoggingSetup(options: LoggingOptions = {}) {
  useEffect(() => {
    // Default to enabled in development, disabled in production
    const isDevelopment = process.env.NODE_ENV === 'development';
    const defaultEnabled = isDevelopment;
    
    const enabled = options.enabled ?? defaultEnabled;
    
    // Only set up logging if enabled
    if (!enabled) return;
    
    // Initialize LogManager (sets up environment variables and browser commands)
    LogManager.initialize();
    
    // Set global log level
    if (options.logLevel !== undefined) {
      LogManager.setGlobalLevel(options.logLevel);
    } else if (isDevelopment) {
      // In development, default to DEBUG level
      LogManager.setGlobalLevel(LogLevel.DEBUG);
    } else {
      // In production, default to INFO level
      LogManager.setGlobalLevel(LogLevel.INFO);
    }
    
    // Set timestamp format if specified
    if (options.timestampFormat) {
      LogManager.setGlobalTimestampFormat(options.timestampFormat);
    }
    
    // Configure browser console commands
    if (typeof window !== 'undefined' && options.browserConsoleCommands) {
      // Setup is handled by LogManager.initialize(), just log the info
      setTimeout(() => {
        console.info(
          "📝 Logging commands available via window.__logger in the console:\n" +
          "- List channels: window.__logger.listChannels()\n" +
          "- Enable channel: window.__logger.enableChannel('channel-name')\n" +
          "- Disable channel: window.__logger.disableChannel('channel-name')\n" +
          "- Set log level: window.__logger.setGlobalLevel('debug')"
        );
      }, 0);
    }
    
    // Enable specific channels if provided
    if (options.enabledChannels && options.enabledChannels.length > 0) {
      options.enabledChannels.forEach(channel => {
        LogManager.setChannelEnabled(channel, true);
      });
    }
    
    // Disable specific channels if provided
    if (options.disabledChannels && options.disabledChannels.length > 0) {
      options.disabledChannels.forEach(channel => {
        LogManager.setChannelEnabled(channel, false);
      });
    }
    
    return () => {
      // No cleanup necessary as LogManager is a singleton
    };
  }, [
    options.enabled,
    options.logLevel,
    options.browserConsoleCommands,
    options.timestampFormat,
  ]);
}